/**
 * Auth Service - Firebase Authentication
 * 
 * Supports:
 * - Anonymous sign-in (legacy)
 * - Passwordless email link sign-in (new EUMPWU flow)
 * - Account linking (anonymous → email)
 */

import { 
  getAuth, 
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  signInAnonymously as firebaseSignInAnonymously,
  signInWithCustomToken,
  linkWithCredential,
  EmailAuthProvider,
  User,
  ActionCodeSettings,
} from 'firebase/auth';

// Key for storing email in localStorage (needed to complete sign-in)
const EMAIL_FOR_SIGN_IN_KEY = 'emailForSignIn';
// Key for storing anonymous UID before linking (for data migration)
const ANONYMOUS_UID_FOR_LINKING_KEY = 'anonymousUidForLinking';

/**
 * AuthService class - maintains backwards compatibility with useAuth hook
 */
export class AuthService {
  static onAuthStateChanged(callback: (user: User | null) => void) {
    const auth = getAuth();
    return onAuthStateChanged(auth, callback);
  }

  static async signInAnonymously(): Promise<User> {
    const auth = getAuth();
    const result = await firebaseSignInAnonymously(auth);
    return result.user;
  }

  static async signOut(): Promise<void> {
    const auth = getAuth();
    await firebaseSignOut(auth);
  }

  static getCurrentUser(): User | null {
    const auth = getAuth();
    return auth.currentUser;
  }
}

/**
 * Get the action code settings for email link sign-in
 */
function getActionCodeSettings(): ActionCodeSettings {
  // URL to redirect back to after clicking the email link
  // This should be your app's URL that handles the sign-in completion
  const baseUrl = window.location.origin;
  
  return {
    // URL to redirect to after clicking the link
    url: `${baseUrl}/auth/complete`,
    // Must be true for email link sign-in
    handleCodeInApp: true,
    // iOS app bundle ID (if you have one)
    // iOS: { bundleId: 'com.ionoff.app' },
    // Android package name (if you have one)
    // android: { packageName: 'com.ionoff.app', installApp: true, minimumVersion: '12' },
    // Dynamic link domain (if using Firebase Dynamic Links)
    // dynamicLinkDomain: 'ionoff.page.link',
  };
}

/**
 * Send a magic link to the user's email
 * Also stores the current anonymous UID (if any) for account linking
 * 
 * @param email - User's email address
 * @returns Promise that resolves when email is sent
 */
export async function sendMagicLink(email: string): Promise<void> {
  const auth = getAuth();
  const actionCodeSettings = getActionCodeSettings();

  try {
    // If user is currently anonymous, save their UID for account linking
    const currentUser = auth.currentUser;
    if (currentUser && currentUser.isAnonymous) {
      window.localStorage.setItem(ANONYMOUS_UID_FOR_LINKING_KEY, currentUser.uid);
      console.log('📧 [Auth] Saved anonymous UID for linking:', currentUser.uid);
    }

    await sendSignInLinkToEmail(auth, email, actionCodeSettings);
    
    // Save the email locally so we can complete sign-in when they click the link
    // This is needed because the email link will open in a new context
    window.localStorage.setItem(EMAIL_FOR_SIGN_IN_KEY, email);
    
    console.log('📧 [Auth] Magic link sent to:', email);
  } catch (error: any) {
    console.error('📧 [Auth] Error sending magic link:', error);
    throw new Error(getAuthErrorMessage(error.code));
  }
}

/**
 * Get the anonymous UID that was saved for linking (if any)
 */
export function getAnonymousUidForLinking(): string | null {
  return window.localStorage.getItem(ANONYMOUS_UID_FOR_LINKING_KEY);
}

/**
 * Clear the stored anonymous UID
 */
export function clearAnonymousUidForLinking(): void {
  window.localStorage.removeItem(ANONYMOUS_UID_FOR_LINKING_KEY);
}

/**
 * Check if the current URL is a sign-in email link
 * 
 * @returns true if the current URL is a valid sign-in link
 */
export function isEmailSignInLink(): boolean {
  const auth = getAuth();
  return isSignInWithEmailLink(auth, window.location.href);
}

/**
 * Complete sign-in using the email link
 * Called when user clicks the magic link in their email
 * 
 * @param email - Optional email (will try to get from localStorage if not provided)
 * @returns The signed-in user
 */
export async function completeSignInWithEmailLink(email?: string): Promise<User> {
  const auth = getAuth();
  
  // Try to get email from parameter or localStorage
  let userEmail = email || window.localStorage.getItem(EMAIL_FOR_SIGN_IN_KEY);
  
  if (!userEmail) {
    // If no email stored (e.g., different device), prompt user
    // This will be handled by the UI
    throw new Error('EMAIL_REQUIRED');
  }

  try {
    const result = await signInWithEmailLink(auth, userEmail, window.location.href);
    
    // Clear the stored email
    window.localStorage.removeItem(EMAIL_FOR_SIGN_IN_KEY);
    
    console.log('✅ [Auth] Sign-in completed for:', result.user.email);
    
    return result.user;
  } catch (error: any) {
    console.error('❌ [Auth] Error completing sign-in:', error);
    throw new Error(getAuthErrorMessage(error.code));
  }
}

/**
 * Get the stored email (if any) for completing sign-in
 */
export function getStoredEmailForSignIn(): string | null {
  return window.localStorage.getItem(EMAIL_FOR_SIGN_IN_KEY);
}

/**
 * Clear the stored email
 */
export function clearStoredEmail(): void {
  window.localStorage.removeItem(EMAIL_FOR_SIGN_IN_KEY);
}

/**
 * Sign out the current user
 */
export async function signOut(): Promise<void> {
  const auth = getAuth();
  await firebaseSignOut(auth);
  console.log('👋 [Auth] User signed out');
}

/**
 * Get the current user (if signed in)
 */
export function getCurrentUser(): User | null {
  const auth = getAuth();
  return auth.currentUser;
}

/**
 * Subscribe to auth state changes
 * 
 * @param callback - Function to call when auth state changes
 * @returns Unsubscribe function
 */
export function onAuthChange(callback: (user: User | null) => void): () => void {
  const auth = getAuth();
  return onAuthStateChanged(auth, callback);
}

/**
 * Check if user is signed in with a verified email (not anonymous)
 */
export function isAuthenticatedWithEmail(): boolean {
  const auth = getAuth();
  const user = auth.currentUser;
  return user !== null && !user.isAnonymous && user.email !== null;
}

/**
 * Get user-friendly error messages for Firebase Auth errors
 */
function getAuthErrorMessage(errorCode: string): string {
  switch (errorCode) {
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/user-disabled':
      return 'This account has been disabled.';
    case 'auth/invalid-action-code':
      return 'This link has expired or already been used. Please request a new one.';
    case 'auth/expired-action-code':
      return 'This link has expired. Please request a new one.';
    case 'auth/operation-not-allowed':
      return 'Email link sign-in is not enabled. Please contact support.';
    case 'auth/too-many-requests':
      return 'Too many requests. Please wait a moment and try again.';
    default:
      return 'An error occurred. Please try again.';
  }
}

// ============================================
// Stripe Payment Sign-In (Auto sign-in after payment)
// ============================================

// Key for storing pending prompt before Stripe redirect
const PENDING_PROMPT_KEY = 'pendingPromptBeforePayment';

/**
 * Sign in using a Firebase custom token
 * Used after Stripe payment to auto-sign in the user
 */
export async function signInWithToken(customToken: string): Promise<User> {
  const auth = getAuth();
  const result = await signInWithCustomToken(auth, customToken);
  console.log('✅ [Auth] Signed in with custom token:', result.user.email);
  return result.user;
}

/**
 * Store the pending prompt before redirecting to Stripe
 * This allows us to automatically send it after payment completes
 */
export function storePendingPrompt(prompt: string, plusHuman?: string): void {
  const data = { prompt, plusHuman, timestamp: Date.now() };
  window.localStorage.setItem(PENDING_PROMPT_KEY, JSON.stringify(data));
  console.log('💾 [Auth] Stored pending prompt for after payment');
}

/**
 * Get and clear the pending prompt (if any)
 * Returns null if no pending prompt or if it's too old (> 30 min)
 */
export function getPendingPrompt(): { prompt: string; plusHuman?: string } | null {
  const stored = window.localStorage.getItem(PENDING_PROMPT_KEY);
  if (!stored) return null;
  
  try {
    const data = JSON.parse(stored);
    // Check if it's not too old (30 minutes max)
    const age = Date.now() - data.timestamp;
    if (age > 30 * 60 * 1000) {
      window.localStorage.removeItem(PENDING_PROMPT_KEY);
      return null;
    }
    
    // Clear it after retrieval
    window.localStorage.removeItem(PENDING_PROMPT_KEY);
    
    return { prompt: data.prompt, plusHuman: data.plusHuman };
  } catch {
    window.localStorage.removeItem(PENDING_PROMPT_KEY);
    return null;
  }
}

/**
 * Clear the pending prompt without retrieving it
 */
export function clearPendingPrompt(): void {
  window.localStorage.removeItem(PENDING_PROMPT_KEY);
}
